import { Plus, X } from "lucide-react";
import * as React from "react";

import {
  type FilterDefinition,
  FilterValueInput,
  effectiveComparators,
  getDefaultsForVariant,
  isValueless,
  formatFilterValue,
  comparatorLabel,
  comparatorLabelForFilter,
  valueSyntax,
} from "@/shared/components/filters";
import {
  type Filter,
  type FilterComparator,
} from "@/shared/domain/query/filters";
import {
  Card,
  Combobox,
  Field,
  RobotoButton,
  Select,
  type SelectItem,
} from "@/shared/foundation/primitives";

import { type ConditionValues } from "../TriggerForm/conditionValues";

import styles from "./TriggerConditionBuilder.module.css";

interface TriggerConditionBuilderHandle {
  /** Focus the value in error, or the cards if none is. */
  focus: () => void;
}

interface TriggerConditionBuilderProps {
  value: ConditionValues;
  onChange: (value: ConditionValues) => void;
  /** Fields on offer for the subscribed events. */
  definitions: FilterDefinition[];
  /** What is wrong with each card's value, by position. */
  rowErrors?: readonly (string | undefined)[];
}

/**
 * A trigger's condition as one card per clause, every one of which must
 * match. Each card picks a field, a comparator the field allows, and the
 * value input the field's type calls for — the same inputs the data tables'
 * filter menu uses, so a card sends what that filter would.
 *
 * A stored condition the cards cannot represent (nesting, NOT, an OR of
 * clauses) is shown read-only and never changed.
 */
export const TriggerConditionBuilder = React.forwardRef<
  TriggerConditionBuilderHandle,
  TriggerConditionBuilderProps
>(function TriggerConditionBuilder(
  { value, onChange, definitions, rowErrors = [] },
  ref,
) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  /** Focus the value the error is about; report whether one was found. */
  const focusInvalidValue = React.useCallback((): boolean => {
    const invalid =
      wrapperRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      null;
    invalid?.focus();
    return invalid !== null;
  }, []);

  // A handle rather than the element, so a form library asking for the focus
  // is sent on to the input the error is about. With none to find — a stored
  // condition, or an error about the set — the wrapper takes it instead.
  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        if (!focusInvalidValue()) {
          wrapperRef.current?.focus();
        }
      },
    }),
    [focusInvalidValue],
  );

  const invalidRow = rowErrors.findIndex((error) => error !== undefined);

  // react-hook-form focuses as soon as it has validated, which is before React
  // has painted the error — measured here, both of its attempts find nothing
  // invalid — so the hand-off is made again from this side when the error
  // arrives. Only ever out of the wrapper's own focus, which is the form having
  // sent the person here, so typing elsewhere is never interrupted.
  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (
      invalidRow >= 0 &&
      wrapper !== null &&
      wrapper.ownerDocument.activeElement === wrapper
    ) {
      focusInvalidValue();
    }
  }, [invalidRow, focusInvalidValue]);

  if (value.type === "stored") {
    return (
      <div
        ref={wrapperRef}
        tabIndex={-1}
        className={styles.notice}
        data-testid="condition-readonly-notice"
      >
        This condition was written through the SDK in a shape this editor keeps
        but cannot change.
        <details>
          <summary>Stored form</summary>
          <pre>{JSON.stringify(value.condition, null, 2)}</pre>
        </details>
      </div>
    );
  }

  const { rows } = value;
  const setRows = (next: Filter[]) => {
    onChange({ type: "rows", rows: next });
  };
  const firstDefinition = definitions.at(0);

  return (
    <div
      ref={wrapperRef}
      // Fallback target for the handle; `-1` is programmatic-only.
      tabIndex={-1}
      className={styles.builder}
      data-testid="trigger-condition-builder"
    >
      {rows.map((row, index) => (
        <ConditionCard
          // Rows carry no identity of their own; every input is controlled,
          // so a shifted index re-renders the right values.
          key={index}
          index={index}
          row={row}
          definitions={definitions}
          error={rowErrors[index]}
          onChange={(next) => {
            setRows(rows.map((existing, i) => (i === index ? next : existing)));
          }}
          onRemove={() => {
            setRows(rows.filter((_, i) => i !== index));
          }}
        />
      ))}
      <RobotoButton
        variant="secondary"
        icon={<Plus aria-hidden />}
        disabled={firstDefinition === undefined}
        onClick={() => {
          if (firstDefinition !== undefined) {
            setRows([...rows, getDefaultsForVariant(firstDefinition)]);
          }
        }}
        data-testid="trigger-add-condition"
      >
        Add condition
      </RobotoButton>
    </div>
  );
});

interface ConditionCardProps {
  index: number;
  row: Filter;
  definitions: FilterDefinition[];
  error: string | undefined;
  onChange: (row: Filter) => void;
  onRemove: () => void;
}

function ConditionCard({
  index,
  row,
  definitions,
  error,
  onChange,
  onRemove,
}: ConditionCardProps) {
  const definition = definitionFor(row, definitions);
  const offered = withRowField(definitions, definition);
  const comparatorItems: SelectItem<FilterComparator>[] = effectiveComparators(
    definition,
  ).map((comparator) => ({
    value: comparator,
    label: comparatorLabel(definition.type, comparator),
  }));
  const testId = `trigger-condition-${index}`;

  return (
    <Card.Root size="compact" data-testid={testId}>
      <Card.Header>
        <Card.Title>{index === 0 ? "If" : "And"}</Card.Title>
        <Card.Summary>{summarize(row, definition)}</Card.Summary>
        <Card.Actions>
          <RobotoButton
            variant="ghost"
            icon={<X aria-hidden />}
            aria-label="Remove condition"
            onClick={onRemove}
            data-testid={`${testId}-remove`}
          />
        </Card.Actions>
      </Card.Header>
      <Card.Body>
        <div className={styles.fields}>
          <div data-testid={`${testId}-field`}>
            <Combobox<FilterDefinition>
              items={offered}
              value={definition}
              onValueChange={(picked) => {
                if (picked !== null && picked.field !== row.field) {
                  onChange(getDefaultsForVariant(picked));
                }
              }}
              itemToStringLabel={(item) => item.name}
              isItemEqualToValue={(item, value) => item.field === value.field}
              label="Field"
              size="lg"
              autoHighlight
            >
              <Combobox.TriggerInput placeholder="Search fields" />
              <Combobox.Content>
                <Combobox.Empty />
                <Combobox.List>
                  {(item: FilterDefinition) => (
                    <Combobox.Item key={item.field} value={item} />
                  )}
                </Combobox.List>
              </Combobox.Content>
            </Combobox>
          </div>
          <Select<FilterComparator>
            label="Comparator"
            items={comparatorItems}
            value={row.comparator}
            onValueChange={(comparator) => {
              if (comparator !== null) {
                onChange(withComparator(row, comparator));
              }
            }}
            size="lg"
          />
          {!isValueless(row.comparator) && (
            <Field.Root invalid={error !== undefined}>
              <Field.Label>Value</Field.Label>
              <FilterValueInput
                filter={row}
                definition={definition}
                autoFocus={false}
                size="lg"
                invalid={error !== undefined}
                onChange={(values) => {
                  onChange({ ...row, values } as Filter);
                }}
              />
              {valueSyntax(row.type, row.comparator) === "like-pattern" && (
                <Field.Description data-testid="trigger-condition-value-syntax">
                  Example: <code>%.parquet</code> matches any value ending in
                  .parquet
                </Field.Description>
              )}
              <Field.Error match>{error}</Field.Error>
            </Field.Root>
          )}
        </div>
      </Card.Body>
    </Card.Root>
  );
}

/**
 * The definition a row was built from. A stored field the offered vocabulary
 * lacks (a metadata key the org list dropped) gets one read off the row, so
 * its card still edits.
 */
function definitionFor(
  row: Filter,
  definitions: readonly FilterDefinition[],
): FilterDefinition {
  const known = definitions.find((offered) => offered.field === row.field);
  if (known !== undefined) {
    return known;
  }
  const base = { field: row.field, name: row.field };
  switch (row.type) {
    case "set":
    case "enum":
      return { ...base, type: row.type, options: [] };
    case "identity":
    case "metric":
      return { ...base, type: "string" };
    default:
      return { ...base, type: row.type };
  }
}

/** The offered fields, with the row's own added when the list lacks it. */
function withRowField(
  definitions: readonly FilterDefinition[],
  definition: FilterDefinition,
): readonly FilterDefinition[] {
  return definitions.some((offered) => offered.field === definition.field)
    ? definitions
    : [definition, ...definitions];
}

/**
 * The row under a new comparator. Values carry over within one field, except
 * where the comparator takes none, or names a different directory to pick
 * from (an identity's principal type).
 */
function withComparator(row: Filter, comparator: FilterComparator): Filter {
  const clears = isValueless(comparator) || row.type === "identity";
  return {
    ...row,
    comparator,
    values: clears ? [] : row.values,
  } as Filter;
}

/** One line restating the card: field, comparator, and value. */
function summarize(row: Filter, definition: FilterDefinition): string {
  const head = `${definition.name} ${comparatorLabelForFilter(row)}`;
  if (isValueless(row.comparator)) {
    return head;
  }
  const formatted = formatFilterValue(row);
  if (formatted === null || formatted.text === "") {
    return `${head} …`;
  }
  const more = formatted.more === undefined ? "" : ` +${formatted.more}`;
  return `${head} ${formatted.text}${more}`;
}
